import { useTheme } from "next-themes"
import { Toaster as Sonner, toast as sonnerToast } from "sonner"

/**
 * Toast Duration Standards (in milliseconds):
 * - Success: 12s - Good news, give users time to read details
 * - Info: 10s - Informational, moderate reading time
 * - Warning: 15s - Important issues, longer but not infinite (prevents pile-up)
 * - Error: 20s - Critical issues, longest duration for user to read and act
 * 
 * Previously warning/error were Infinity which caused toast pile-up issues.
 * Now using generous but finite durations. Users can still dismiss early.
 */
const TOAST_DURATIONS = {
  SUCCESS: 12000,
  INFO: 10000,
  WARNING: 15000,
  ERROR: 20000,
  DEFAULT: 10000,
};

/**
 * DEDUPLICATION SYSTEM
 * Prevents the same toast message from showing multiple times within a short window.
 * This fixes issues where:
 * - Cascading error handlers all show toasts
 * - Loop operations show per-item toasts
 * - Both success and error paths fire
 * - Parent and child components both show toasts
 */
const recentToasts = new Map(); // message hash -> timestamp
const DEDUP_WINDOW_MS = 3000; // Don't show same toast within 3 seconds

// Generate a hash for deduplication
function hashMessage(message) {
  if (typeof message === 'string') {
    return message.toLowerCase().trim();
  }
  // For React elements, use a simplified string representation
  if (message?.props) {
    try {
      return JSON.stringify(message.props).slice(0, 100);
    } catch {
      return String(message);
    }
  }
  return String(message);
}

// Check if we should show this toast (deduplication check)
function shouldShowToast(message, options = {}) {
  // Skip deduplication if explicitly disabled
  if (options.allowDuplicate) {
    return true;
  }
  
  const hash = hashMessage(message);
  const now = Date.now();
  
  // Check if same message was shown recently
  const lastShown = recentToasts.get(hash);
  if (lastShown && (now - lastShown) < DEDUP_WINDOW_MS) {
    return false; // Duplicate - don't show
  }
  
  // Mark as shown
  recentToasts.set(hash, now);
  
  // Clean up old entries periodically (prevent memory leak)
  if (recentToasts.size > 50) {
    for (const [key, time] of recentToasts) {
      if (now - time > DEDUP_WINDOW_MS) {
        recentToasts.delete(key);
      }
    }
  }
  
  return true;
}

// Generate stable IDs for common toast patterns to prevent stacking
function generateToastId(type, message, options = {}) {
  if (options.id) return options.id;
  
  if (typeof message === 'string') {
    const lowerMsg = message.toLowerCase();
    // Network errors - single toast
    if (lowerMsg.includes('network') || lowerMsg.includes('connection')) {
      return 'network-error';
    }
    // Upload-related - single toast per upload session
    if (lowerMsg.includes('upload')) {
      return `upload-${type}`;
    }
    // Dashboard loading
    if (lowerMsg.includes('dashboard')) {
      return `dashboard-${type}`;
    }
    // Subscription/trial messages
    if (lowerMsg.includes('trial') || lowerMsg.includes('subscription')) {
      return `subscription-${type}`;
    }
  }
  return undefined; // Let sonner generate ID
}

// Custom toast wrapper that applies deduplication and better defaults
const createToast = (message, options = {}) => {
  if (!shouldShowToast(message, options)) return null;
  const id = generateToastId('default', message, options);
  return sonnerToast(message, { duration: TOAST_DURATIONS.DEFAULT, ...options, id: id || options.id });
};

// Attach methods to the callable function with deduplication
createToast.success = (message, options = {}) => {
  if (!shouldShowToast(message, options)) return null;
  const id = generateToastId('success', message, options);
  return sonnerToast.success(message, { duration: TOAST_DURATIONS.SUCCESS, ...options, id: id || options.id });
};

createToast.info = (message, options = {}) => {
  if (!shouldShowToast(message, options)) return null;
  const id = generateToastId('info', message, options);
  return sonnerToast.info(message, { duration: TOAST_DURATIONS.INFO, ...options, id: id || options.id });
};

createToast.warning = (message, options = {}) => {
  if (!shouldShowToast(message, options)) return null;
  const id = generateToastId('warning', message, options);
  return sonnerToast.warning(message, { duration: TOAST_DURATIONS.WARNING, ...options, id: id || options.id });
};

createToast.error = (message, options = {}) => {
  if (!shouldShowToast(message, options)) return null;
  const id = generateToastId('error', message, options);
  return sonnerToast.error(message, { duration: TOAST_DURATIONS.ERROR, ...options, id: id || options.id });
};

createToast.message = (message, options = {}) => {
  if (!shouldShowToast(message, options)) return null;
  return sonnerToast(message, { duration: TOAST_DURATIONS.DEFAULT, ...options });
};

createToast.promise = sonnerToast.promise;
createToast.custom = sonnerToast.custom;
createToast.dismiss = sonnerToast.dismiss;
createToast.DURATIONS = TOAST_DURATIONS;

// Export the callable toast function
const toast = createToast;

const Toaster = ({
  ...props
}) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      position="top-right"
      duration={TOAST_DURATIONS.DEFAULT}
      closeButton
      visibleToasts={2}
      expand={true}
      gap={8}
      toastOptions={{
        className: "cursor-pointer",
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
        onDismiss: () => {},
      }}
      {...props} />
  );
}

export { Toaster, toast }
